import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MdWorkspacePremium, MdArrowUpward, MdCheckCircleOutline, MdRemoveCircleOutline } from 'react-icons/md';
import { loadStripe } from '@stripe/stripe-js';

import { fetchMyPlan, startOrganizationSubscription } from '../../../services/Http/Api';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import MainButton from '../../../shared/layout/buttons/MainButton';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
    ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
    : null;

/**
 * "Moj plan" — što organizacija ima, koliko je potrošila i što dobiva
 * nadogradnjom.
 *
 * Namjerno prikazuje potrošnju uz svaki limit, a ne samo popis značajki:
 * besplatni korisnik koji ne vidi svoj rezultat nema razloga platiti.
 */
const MyPlan = () => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [checkoutError, setCheckoutError] = useState('');
    const [checkoutMessage, setCheckoutMessage] = useState('');
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [selectedPlanCode, setSelectedPlanCode] = useState('');

    useEffect(() => {
        let cancelled = false;

        fetchMyPlan()
            .then((response) => {
                if (!cancelled) {
                    setData(response);
                    setSelectedPlanCode(response.upgrade_plans?.[0]?.code || response.next_plan?.code || '');
                }
            })
            .catch(() => { if (!cancelled) setError(t('Plan.loadFailed')); })
            .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    }, [t]);

    if (loading) return <LoadingMessage />;
    if (error) return <ErrorMessage>{error}</ErrorMessage>;
    if (!data) return null;

    const upgradePlans = data.upgrade_plans || (data.next_plan ? [data.next_plan] : []);
    const selectedPlan = upgradePlans.find((plan) => plan.code === selectedPlanCode) || upgradePlans[0] || null;
    const hasStripeSubscription = data.subscription?.source === 'stripe';
    const entitlementRows = data.entitlements.filter((row) => row.key !== 'discovery.monthly_reach');
    const usageRows = entitlementRows.filter((row) => row.type === 'integer');
    const featureRows = entitlementRows.filter((row) => row.type !== 'integer');

    const price = (cents, currency, period = 'monthly') => {
        if (cents === 0) return t('Plan.free');
        const periodLabel = ['year', 'yearly', 'annual'].includes(String(period).toLowerCase())
            ? t('Plan.perYear')
            : t('Plan.perMonth');
        return `${(cents / 100).toFixed(2)} ${currency}/${periodLabel}`;
    };

    const startCheckout = async () => {
        setCheckoutError('');
        setCheckoutMessage('');
        if (!stripePromise) {
            setCheckoutError(t('Plan.paymentUnavailable'));
            return;
        }
        setCheckoutLoading(true);
        try {
            const stripe = await stripePromise;
            const checkout = await startOrganizationSubscription(selectedPlan.code);
            if (checkout.client_secret) {
                const result = await stripe.confirmCardPayment(checkout.client_secret);
                if (result.error) throw new Error(result.error.message || t('Plan.paymentFailed'));
                if (!['succeeded', 'processing'].includes(result.paymentIntent?.status)) {
                    throw new Error(t('Plan.paymentNotConfirmed'));
                }
            } else if (checkout.status !== 'active') {
                throw new Error(t('Plan.paymentNotConfirmed'));
            }

            let updatedPlan = null;
            for (let attempt = 0; attempt < 10; attempt += 1) {
                updatedPlan = await fetchMyPlan();
                if (updatedPlan.plan?.code === selectedPlan.code
                    && ['active', 'trialing'].includes(updatedPlan.subscription?.status)) break;
                await new Promise((resolve) => window.setTimeout(resolve, 1000));
            }
            setData(updatedPlan || await fetchMyPlan());
            setCheckoutMessage(updatedPlan?.plan?.code === selectedPlan.code
                && ['active', 'trialing'].includes(updatedPlan.subscription?.status)
                ? t('Plan.paymentReceived')
                : t('Plan.paymentActivationPending'));
        } catch (requestError) {
            const response = requestError.response?.data;
            const message = response?.error || response?.message || requestError.message;
            if (message?.includes('Add a payment method')) {
                window.location.assign('/settings');
            } else {
                setCheckoutError(message || t('Plan.paymentFailed'));
            }
        } finally {
            setCheckoutLoading(false);
        }
    };

    const formatLimit = (row) => {
        if (row.unlimited) return t('Plan.unlimited');
        if (row.type === 'boolean') return row.limit ? t('Plan.included') : t('Plan.notIncluded');
        if (row.limit === null || row.limit === undefined) return '—';
        return typeof row.limit === 'number' ? row.limit.toLocaleString('hr-HR') : row.limit;
    };

    const formatNext = (row) => {
        if (row.next_plan_limit === null || row.next_plan_limit === undefined) return null;
        if (row.next_plan_limit === 'unlimited') return t('Plan.unlimited');
        if (typeof row.next_plan_limit === 'boolean') {
            return row.next_plan_limit ? t('Plan.included') : t('Plan.notIncluded');
        }
        return typeof row.next_plan_limit === 'number'
            ? row.next_plan_limit.toLocaleString('hr-HR')
            : row.next_plan_limit;
    };

    const selectedPlanLimit = (row) => selectedPlan?.limits?.[row.key] ?? row.next_plan_limit;
    const selectedPlanHasLowerLimit = selectedPlan && entitlementRows.some((row) => {
        const nextLimit = selectedPlanLimit(row);
        if (nextLimit === null || nextLimit === undefined) return false;
        if (row.type === 'boolean') return Boolean(row.limit) && nextLimit === false;
        if (row.unlimited) return nextLimit !== 'unlimited';
        if (typeof row.limit === 'number' && typeof nextLimit === 'number') return nextLimit < row.limit;
        return false;
    });

    return (
        <Wrapper>
            <PageTitle icon={<MdWorkspacePremium size={28} />}>{t('Plan.title')}</PageTitle>

            <PlanCard>
                <PlanHeader>
                    <CurrentPlanPanel>
                        <PlanIcon><MdWorkspacePremium size={26} /></PlanIcon>
                        <CurrentPlanDetails>
                            <CurrentPlanEyebrow>{t('Plan.currentPlan')}</CurrentPlanEyebrow>
                            <PlanName>{data.plan?.name ?? '—'}</PlanName>
                            <PlanPrice>{data.plan ? price(data.plan.price_cents, data.plan.currency, data.plan.billing_period) : ''}</PlanPrice>
                            {data.subscription?.current_period_end && (
                                <PeriodNote>
                                    {t('Plan.renewsOn')}{' '}
                                    {new Date(data.subscription.current_period_end).toLocaleDateString('hr-HR')}
                                </PeriodNote>
                            )}
                        </CurrentPlanDetails>
                    </CurrentPlanPanel>

                    {selectedPlan && (
                        <UpgradeBox>
                            <UpgradeTopline>
                                <UpgradeLabel>{t('Plan.choosePlan')}</UpgradeLabel>
                                <UpgradeIcon><MdArrowUpward size={18} /></UpgradeIcon>
                            </UpgradeTopline>
                            {upgradePlans.length > 1 && (
                                <PlanSelect
                                    value={selectedPlan.code}
                                    onChange={(event) => setSelectedPlanCode(event.target.value)}
                                    aria-label={t('Plan.upgradeTo')}
                                >
                                    {upgradePlans.map((plan) => (
                                        <option key={plan.code} value={plan.code}>
                                            {plan.name}{plan.private_offer ? ` · ${t('Plan.privateOffer')}` : ''}
                                        </option>
                                    ))}
                                </PlanSelect>
                            )}
                            <UpgradeSummary>
                                <UpgradeName>{selectedPlan.name}</UpgradeName>
                                <UpgradePrice>{price(selectedPlan.price_cents, selectedPlan.currency, selectedPlan.billing_period)}</UpgradePrice>
                            </UpgradeSummary>
                            <UpgradeHint>{selectedPlanHasLowerLimit ? t('Plan.differentLimits') : t('Plan.compareBelow')}</UpgradeHint>
                            {hasStripeSubscription ? (
                                <CheckoutFeedback>{t('Plan.existingStripeSubscription')}</CheckoutFeedback>
                            ) : (
                                <UpgradeButton type="button" onClick={startCheckout} disabled={checkoutLoading}>
                                    <MdArrowUpward /> {checkoutLoading ? t('Plan.paymentStarting') : (selectedPlanHasLowerLimit ? t('Plan.changePlan') : t('Plan.upgrade'))}
                                </UpgradeButton>
                            )}
                            {checkoutError && <CheckoutFeedback $error>{checkoutError}</CheckoutFeedback>}
                            {checkoutMessage && <CheckoutFeedback>{checkoutMessage}</CheckoutFeedback>}
                        </UpgradeBox>
                    )}
                </PlanHeader>
            </PlanCard>

            <PlanCard>
                <SectionIntro>
                    <SectionHeading>{t('Plan.usageTitle')}</SectionHeading>
                    <SectionDescription>{t('Plan.usageSubtitle')}</SectionDescription>
                </SectionIntro>

                <UsageGrid>
                    {usageRows.map((row) => {
                        const nearLimit = row.percent !== null && row.percent >= 80;
                        const selectedLimit = formatNext({ ...row, next_plan_limit: selectedPlanLimit(row) });

                        return (
                            <UsageTile key={row.key}>
                                <UsageTileHeader>
                                    <div>
                                        <UsageName>{row.name}</UsageName>
                                        {row.unit && <Unit>{row.unit}</Unit>}
                                    </div>
                                    {row.unlimited && <UnlimitedBadge>{t('Plan.unlimited')}</UnlimitedBadge>}
                                </UsageTileHeader>
                                <UsageAmount>{row.used === null ? '—' : row.used.toLocaleString('hr-HR')}</UsageAmount>
                                <UsageAmountLabel>{t('Plan.used')}</UsageAmountLabel>
                                {row.percent !== null && (
                                    <ProgressTrack aria-label={`${row.percent}% ${t('Plan.used').toLowerCase()}`}>
                                        <ProgressFill $percent={row.percent} $warn={nearLimit} />
                                    </ProgressTrack>
                                )}
                                <UsageTileFooter>
                                    <LimitDetail>
                                        <span>{t('Plan.currentLimit')}</span>
                                        <strong>{formatLimit(row)}</strong>
                                    </LimitDetail>
                                    {selectedPlan && (
                                        <LimitDetail $comparison>
                                            <span>{selectedPlan.name}</span>
                                            <strong>{selectedLimit ?? '—'}</strong>
                                        </LimitDetail>
                                    )}
                                </UsageTileFooter>
                            </UsageTile>
                        );
                    })}
                </UsageGrid>

                {featureRows.length > 0 && (
                    <FeatureSection>
                        <FeatureHeading>{t('Plan.featuresTitle')}</FeatureHeading>
                        <FeatureGrid>
                            {featureRows.map((row) => {
                                const selectedValue = selectedPlanLimit(row);
                                const currentIncluded = Boolean(row.limit);

                                return (
                                    <FeatureTile key={row.key}>
                                        <FeatureName>{row.name}</FeatureName>
                                <FeatureComparisons>
                                    <FeaturePlan>
                                        <span>{data.plan?.name || t('Plan.currentPlan')}</span>
                                        {row.type === 'boolean' ? (
                                            <FeatureState $included={currentIncluded}>
                                                {currentIncluded ? <MdCheckCircleOutline /> : <MdRemoveCircleOutline />}
                                                {currentIncluded ? t('Plan.included') : t('Plan.notIncluded')}
                                            </FeatureState>
                                        ) : <FeatureValue>{formatLimit(row)}</FeatureValue>}
                                    </FeaturePlan>
                                    {selectedPlan && (
                                        <FeaturePlan>
                                            <span>{selectedPlan.name}</span>
                                            {row.type === 'boolean' ? (
                                                <FeatureState $included={Boolean(selectedValue)}>
                                                    {selectedValue ? <MdCheckCircleOutline /> : <MdRemoveCircleOutline />}
                                                    {selectedValue ? t('Plan.included') : t('Plan.notIncluded')}
                                                </FeatureState>
                                            ) : <FeatureValue>{formatNext({ ...row, next_plan_limit: selectedValue }) ?? '—'}</FeatureValue>}
                                        </FeaturePlan>
                                            )}
                                        </FeatureComparisons>
                                    </FeatureTile>
                                );
                            })}
                        </FeatureGrid>
                    </FeatureSection>
                )}

                <FootNote>{t('Plan.liveNote')}</FootNote>
            </PlanCard>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const PlanCard = styled(StyledCard)`
    margin-bottom: 0;
`;

const PlanHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: stretch;
    gap: 1.5rem;
    flex-wrap: wrap;
`;

const CurrentPlanPanel = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    flex: 1 1 280px;
    min-width: 0;
    padding: 1.25rem 0.5rem;
`;

const PlanIcon = styled.div`
    display: grid;
    place-items: center;
    width: 3.25rem;
    height: 3.25rem;
    flex: 0 0 3.25rem;
    color: #6338b5;
    background: #f1ebfb;
    border-radius: 12px;
`;

const CurrentPlanDetails = styled.div`
    min-width: 0;
`;

const CurrentPlanEyebrow = styled.div`
    margin-bottom: 0.3rem;
    color: #667085;
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

const PlanName = styled.h2`
    margin: 0;
    color: #202633;
    font-size: clamp(1.35rem, 2vw, 1.7rem);
    line-height: 1.2;
    overflow-wrap: anywhere;
`;

const PlanPrice = styled.div`
    margin-top: 0.45rem;
    color: #475467;
    font-size: 1rem;
    font-weight: 600;
`;

const PeriodNote = styled.div`
    font-size: 0.85rem;
    opacity: 0.6;
    margin-top: 0.5rem;
`;

const UpgradeBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
    align-items: flex-start;
    flex: 0 1 330px;
    min-width: min(100%, 260px);
    padding: 1.15rem;
    border: 1px solid #e4dcf4;
    border-radius: 12px;
    background: linear-gradient(145deg, #faf8ff 0%, #f4effc 100%);
    box-shadow: 0 4px 14px rgba(70, 38, 120, 0.06);
`;

const UpgradeTopline = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;

const UpgradeLabel = styled.span`
    color: #6b5b85;
    font-size: 0.73rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

const UpgradeIcon = styled.span`
    display: grid;
    place-items: center;
    width: 2rem;
    height: 2rem;
    color: #6338b5;
    background: #fff;
    border: 1px solid #e4dcf4;
    border-radius: 8px;
`;

const UpgradeName = styled.strong`
    color: #202633;
    font-size: 1.2rem;
    line-height: 1.25;
    overflow-wrap: anywhere;
`;

const UpgradeSummary = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
`;

const UpgradePrice = styled.span`
    color: #475467;
    font-size: 0.9rem;
    font-weight: 600;
    text-align: right;
`;

const UpgradeHint = styled.p`
    margin: 0;
    color: #667085;
    font-size: 0.82rem;
    line-height: 1.45;
`;

const PlanSelect = styled.select`
    width: 100%;
    max-width: 100%;
    padding: 0.65rem 0.75rem;
    border: 1px solid #d8cdea;
    border-radius: 8px;
    background: #fff;
    color: #1d2939;
    font: inherit;
    &:focus-visible {
        outline: 3px solid rgba(99, 56, 181, 0.22);
        outline-offset: 1px;
    }
`;

const UpgradeButton = styled(MainButton)`
    width: 100%;
    justify-content: center;
    min-height: 42px;
`;

const CheckoutFeedback = styled.span`
    max-width: 300px;
    color: ${({ $error }) => ($error ? '#b42318' : '#14734a')};
    font-size: 0.84rem;
    line-height: 1.4;
`;

const SectionIntro = styled.div`
    margin-bottom: 1.15rem;
`;

const SectionHeading = styled.h3`
    margin: 0;
    font-size: 1.1rem;
`;

const SectionDescription = styled.p`
    margin: 0.3rem 0 0;
    color: #667085;
    font-size: 0.88rem;
`;

const UsageGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 245px), 1fr));
    gap: 0.85rem;
`;

const UsageTile = styled.div`
    min-width: 0;
    padding: 1rem;
    border: 1px solid #eaecf0;
    border-radius: 12px;
    background: #fff;
    transition: border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease;

    &:hover {
        border-color: #d8cdea;
        box-shadow: 0 6px 18px rgba(16, 24, 40, 0.06);
        transform: translateY(-1px);
    }
`;

const UsageTileHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    min-height: 2.5rem;
`;

const UsageName = styled.strong`
    display: block;
    color: #344054;
    font-size: 0.88rem;
    line-height: 1.35;
`;

const Unit = styled.span`
    display: block;
    margin-top: 0.2rem;
    color: #667085;
    font-size: 0.75rem;
`;

const UnlimitedBadge = styled.span`
    flex: 0 0 auto;
    padding: 0.22rem 0.5rem;
    border-radius: 999px;
    background: #ecfdf3;
    color: #027a48;
    font-size: 0.7rem;
    font-weight: 700;
`;

const UsageAmount = styled.div`
    margin-top: 0.8rem;
    color: #202633;
    font-size: 1.55rem;
    font-weight: 700;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
`;

const UsageAmountLabel = styled.div`
    margin-top: 0.25rem;
    color: #667085;
    font-size: 0.75rem;
`;

const ProgressTrack = styled.div`
    height: 6px;
    margin-top: 0.85rem;
    overflow: hidden;
    border-radius: 999px;
    background: #eaecf0;
`;

const ProgressFill = styled.div`
    width: ${({ $percent }) => $percent}%;
    height: 100%;
    border-radius: inherit;
    background: ${({ $warn }) => ($warn ? '#f79009' : 'linear-gradient(90deg, #8054c7, #6338b5)')};
    transition: width 180ms ease;
`;

const UsageTileFooter = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.65rem;
    margin-top: 0.9rem;
    padding-top: 0.75rem;
    border-top: 1px solid #f0f1f3;
`;

const LimitDetail = styled.div`
    min-width: 0;

    span {
        display: block;
        overflow: hidden;
        color: #667085;
        font-size: 0.7rem;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    strong {
        display: block;
        margin-top: 0.15rem;
        overflow-wrap: anywhere;
        color: ${({ $comparison }) => ($comparison ? '#6338b5' : '#344054')};
        font-size: 0.82rem;
    }
`;

const FeatureSection = styled.section`
    margin-top: 1.5rem;
`;

const FeatureHeading = styled.h4`
    margin: 0 0 0.75rem;
    color: #344054;
    font-size: 0.94rem;
`;

const FeatureGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
    gap: 0.7rem;
`;

const FeatureTile = styled.div`
    min-width: 0;
    padding: 0.9rem 1rem;
    border: 1px solid #eaecf0;
    border-radius: 10px;
    background: #fcfcfd;
`;

const FeatureName = styled.strong`
    display: block;
    margin-bottom: 0.65rem;
    color: #344054;
    font-size: 0.85rem;
    line-height: 1.4;
`;

const FeatureComparisons = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;
`;

const FeaturePlan = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 105px;

    > span:first-child {
        color: #667085;
        font-size: 0.7rem;
    }
`;

const FeatureState = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.28rem;
    color: ${({ $included }) => ($included ? '#027a48' : '#667085')};
    font-size: 0.76rem;
    font-weight: 600;

    svg { font-size: 0.95rem; }
`;

const FeatureValue = styled.strong`
    color: #6338b5;
    font-size: 0.78rem;
`;

const FootNote = styled.p`
    margin: 1rem 0 0;
    font-size: 0.8rem;
    opacity: 0.6;
`;

export default MyPlan;
